import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
    Grid,
    Tabs,
    Tab,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Button,
    CircularProgress,
    Collapse,
    Box,
    Checkbox,
    Typography,
    Chip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '../common/confirmationdialog';
import Ajax from '../modules/ajax';
import {
    getCargoProviders,
    saveCargoProvider,
    getCargoProducts,
    getCargoPurchaseGroups,
    saveCargoPurchaseGroup,
    deleteCargoPurchaseGroup,
    getCargoSalesGroups,
    saveCargoSalesGroup,
    deleteCargoSalesGroup,
    getCargoProviderProducts,
    saveCargoProviderProduct,
    deleteCargoProviderProduct,
    fetchAllSalesGroupContractLinks,
} from '../modules/cargopricingdata';
import { getParameters } from '../modules/parameterdata';
import CargoPurchaseGroupEditor from './cargopurchasegroupeditor';
import CargoSalesGroupEditor from './cargosalesgroupeditor';
import CargoProductProviderEditor from './cargoproductprovidereditor';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    table: {
        marginTop: theme.spacing(1),
    },
    addRowContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    btn: {
        margin: theme.spacing(1),
    },
    overlayContainer: {
        position: 'relative',
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.6)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'auto',
    },
    tabContent: {
        paddingTop: theme.spacing(2),
        width: '100%',
    },
    detailBox: {
        paddingLeft: theme.spacing(6),
        paddingBottom: theme.spacing(1),
        backgroundColor: theme.palette.action.hover,
    },
    detailTable: {
        width: 'auto',
        minWidth: 480,
    },
    detailCell: {
        fontSize: '0.78rem',
        padding: '2px 8px',
        color: theme.palette.text.secondary,
    },
    contractListTitle: {
        marginTop: theme.spacing(1),
        paddingLeft: theme.spacing(1),
    },
    contractChipsWrap: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        paddingLeft: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
}));

function TabPanel({ children, value, index }) {
    if (value !== index) return null;
    return <div>{children}</div>;
}

export default function CargoPricingPanel() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const providers = useSelector((s) => s.cargopricingdata.providers);
    const products = useSelector((s) => s.cargopricingdata.products);
    const purchaseGroups = useSelector((s) => s.cargopricingdata.purchaseGroups);
    const salesGroups = useSelector((s) => s.cargopricingdata.salesGroups);
    const salesGroupContractLinks = useSelector((s) => s.cargopricingdata.salesGroupContractLinks);
    const providerProducts = useSelector((s) => s.cargopricingdata.providerProducts);
    const loading = useSelector((s) => s.cargopricingdata.loading);
    const municipalities = useSelector((s) => (s.postaldata || {}).municipalities);
    const deliveryMethods = useSelector((s) => (s.parameterdata.parameters || {})['DeliveryMethod']);
    const contractNumbers = useSelector((s) => (s.parameterdata.parameters || {})['ContractNumber']);

    const [tab, setTab] = useState(0);

    // Product provider associations (Rahtipalvelut tab)
    const [expandedProducts, setExpandedProducts] = useState({});
    const [productEditorOpen, setProductEditorOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);

    // Purchase group dialog
    const [purchaseGroupEditorOpen, setPurchaseGroupEditorOpen] = useState(false);
    const [editingPurchaseGroup, setEditingPurchaseGroup] = useState(null);
    const [expandedPurchaseGroups, setExpandedPurchaseGroups] = useState({});

    // Sales group dialog
    const [salesGroupEditorOpen, setSalesGroupEditorOpen] = useState(false);
    const [expandedSalesGroups, setExpandedSalesGroups] = useState({});
    const [editingSalesGroup, setEditingSalesGroup] = useState(null);

    // Confirm delete
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    useEffect(() => {
        if (!providers && !loading) dispatch(getCargoProviders());
        if (!products) dispatch(getCargoProducts());
        if (!purchaseGroups) dispatch(getCargoPurchaseGroups());
        if (!salesGroups) dispatch(getCargoSalesGroups());
        if (!salesGroupContractLinks || Object.keys(salesGroupContractLinks).length === 0)
            dispatch(fetchAllSalesGroupContractLinks());
        if (!providerProducts) dispatch(getCargoProviderProducts());
        if (!deliveryMethods) dispatch(getParameters());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Providers ────────────────────────────────────────────────────────────

    const toggleBlockIfPricingMissing = (provider) => {
        dispatch(saveCargoProvider({ ...provider, blockIfPricingMissing: !provider.blockIfPricingMissing }));
    };

    // ── Products / provider associations ────────────────────────────────────

    const toggleProductExpanded = (id) => {
        setExpandedProducts((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const getAssociationsForProduct = (productId) =>
        (providerProducts || []).filter((pp) => pp.cargoProductId === productId);

    const openProductEditor = (product) => {
        setEditingProduct(product);
        setProductEditorOpen(true);
    };

    const closeProductEditor = () => {
        setProductEditorOpen(false);
        setEditingProduct(null);
    };

    const addProviderAssociation = (productId, providerId, deliveryMethodId) => {
        dispatch(
            saveCargoProviderProduct({
                clusterId: 0,
                cargoProviderId: providerId,
                cargoProductId: productId,
                deliveryMethodId: deliveryMethodId || null,
            })
        );
    };

    const updateAssociationDeliveryMethod = (association, deliveryMethodId) => {
        dispatch(saveCargoProviderProduct({ ...association, deliveryMethodId: deliveryMethodId || null }));
    };

    const deleteProviderAssociation = (association) => {
        setDeleteTarget({ type: 'providerProduct', item: association });
        setConfirmOpen(true);
    };

    // ── Purchase Groups ────────────────────────────────────────────────────────

    const openNewPurchaseGroup = () => {
        setEditingPurchaseGroup(null);
        setPurchaseGroupEditorOpen(true);
    };

    const openEditPurchaseGroup = async (group) => {
        try {
            const resp = await Ajax.get(`api/cargopricing/purchasegroup/${group.clusterId}`);
            setEditingPurchaseGroup(resp.data);
        } catch {
            setEditingPurchaseGroup(group);
        }
        setPurchaseGroupEditorOpen(true);
    };

    const savePurchaseGroup = async (group) => {
        await dispatch(saveCargoPurchaseGroup(group));
        setPurchaseGroupEditorOpen(false);
        setEditingPurchaseGroup(null);
    };

    const confirmDeletePurchaseGroup = (group) => {
        setDeleteTarget({ type: 'purchaseGroup', item: group });
        setConfirmOpen(true);
    };

    // ── Sales Groups ───────────────────────────────────────────────────────────

    const openNewSalesGroup = () => {
        setEditingSalesGroup(null);
        setSalesGroupEditorOpen(true);
    };

    const openEditSalesGroup = async (group) => {
        try {
            const resp = await Ajax.get(`api/cargopricing/salesgroup/${group.clusterId}`);
            setEditingSalesGroup(resp.data);
        } catch {
            setEditingSalesGroup(group);
        }
        setSalesGroupEditorOpen(true);
    };

    const saveSalesGroupHandler = async (group) => {
        await dispatch(saveCargoSalesGroup(group));
        setSalesGroupEditorOpen(false);
        setEditingSalesGroup(null);
    };

    const confirmDeleteSalesGroup = (group) => {
        setDeleteTarget({ type: 'salesGroup', item: group });
        setConfirmOpen(true);
    };

    // ── Confirm delete dispatch ────────────────────────────────────────────────

    const doDelete = async () => {
        if (!deleteTarget) return;
        const { type, item } = deleteTarget;
        if (type === 'purchaseGroup') await dispatch(deleteCargoPurchaseGroup(item.clusterId));
        else if (type === 'salesGroup') await dispatch(deleteCargoSalesGroup(item.clusterId));
        else if (type === 'providerProduct') await dispatch(deleteCargoProviderProduct(item.clusterId));
        setConfirmOpen(false);
        setDeleteTarget(null);
    };

    const getProviderName = (id) => {
        if (!providers) return '';
        const p = providers.find((p) => p.clusterId === id);
        return p ? p.name : '';
    };

    const getProductName = (id) => {
        if (!products) return '';
        const p = products.find((p) => p.clusterId === id);
        return p ? p.type : '';
    };

    const getDeliveryMethodName = (id) => {
        if (!deliveryMethods) return '';
        const m = deliveryMethods.find((d) => d.id === id);
        return m ? m.value : '';
    };

    const getMunicipalityNames = (codes) => {
        if (!codes || codes.length === 0) return '';
        return codes
            .map((code) => {
                const m = (municipalities || []).find((m) => m.municipalityCode === code);
                return m ? m.municipalityName : code;
            })
            .join(', ');
    };

    const getSortedContractLinks = (salesGroupId) => {
        const links = salesGroupContractLinks[salesGroupId] || [];
        const withNames = links.map((link) => {
            const contract = (contractNumbers || []).find((c) => c.id === link.contractNumberId);
            return { ...link, contractName: contract ? contract.value.trim() : String(link.contractNumberId) };
        });
        return withNames.sort((a, b) =>
            a.contractName.localeCompare(b.contractName, undefined, { numeric: true, sensitivity: 'base' })
        );
    };

    // Derive effective pricing mode for a group: prefer the persisted field, else infer from
    // its rows for legacy data that predates the explicit pricingMode column.
    const getGroupPricingMode = (group, rows) => {
        if (group && group.pricingMode) return group.pricingMode;
        const list = rows || [];
        if (list.some((r) => r.municipalityCodes && r.municipalityCodes.length > 0)) return 'municipality';
        if (list.some((r) => r.nthPrice && r.nthPrice > 1)) return 'nth';
        return 'zip';
    };

    // Which extra columns (beyond price) to show in the detail table, based on pricing mode
    const modeColumnCount = (mode) => {
        if (mode === 'municipality') return 2; // price, municipalities
        if (mode === 'nth') return 2; // price, nth
        return 3; // zip mode (default/legacy): price, zip start, zip end
    };

    const renderModeHeaderCells = (mode) => {
        if (mode === 'municipality') {
            return <TableCell className={classes.detailCell}>{t('cargo.municipalities')}</TableCell>;
        }
        if (mode === 'nth') {
            return <TableCell className={classes.detailCell}>{t('cargo.nthPrice')}</TableCell>;
        }
        return (
            <>
                <TableCell className={classes.detailCell}>{t('cargo.zipcodeRangeStart')}</TableCell>
                <TableCell className={classes.detailCell}>{t('cargo.zipcodeRangeEnd')}</TableCell>
            </>
        );
    };

    const renderModeValueCells = (mode, row) => {
        if (mode === 'municipality') {
            return <TableCell className={classes.detailCell}>{getMunicipalityNames(row.municipalityCodes)}</TableCell>;
        }
        if (mode === 'nth') {
            return <TableCell className={classes.detailCell}>{row.nthPrice}</TableCell>;
        }
        return (
            <>
                <TableCell className={classes.detailCell}>{row.zipcodeRangeStart}</TableCell>
                <TableCell className={classes.detailCell}>{row.zipcodeRangeEnd}</TableCell>
            </>
        );
    };

    const togglePurchaseGroup = (id) => {
        setExpandedPurchaseGroups((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleSalesGroup = (id) => {
        setExpandedSalesGroups((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const deleteConfirmText = () => {
        if (!deleteTarget) return '';
        const { type, item } = deleteTarget;
        if (type === 'purchaseGroup') return t('cargo.confirmDeletePurchaseGroup', { name: item.name });
        if (type === 'salesGroup') return t('cargo.confirmDeleteSalesGroup', { name: item.name });
        if (type === 'providerProduct')
            return t('cargo.confirmDeleteProviderProduct', { name: getProviderName(item.cargoProviderId) });
        return '';
    };

    return (
        <div className={classes.root}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary">
                <Tab label={t('cargo.providers')} />
                <Tab label={t('cargo.products')} />
                <Tab label={t('cargo.purchaseGroups')} />
                <Tab label={t('cargo.salesGroups')} />
            </Tabs>

            {/* Tab 0 – Providers */}
            <TabPanel value={tab} index={0}>
                <div className={classes.tabContent}>
                    <Grid container className={classes.overlayContainer}>
                        {loading && (
                            <div className={classes.overlay}>
                                <CircularProgress />
                            </div>
                        )}
                        <Grid item xs={12}>
                            <Table className={classes.table} size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('general.name')}</TableCell>
                                        <TableCell>{t('cargo.blockIfPricingMissing')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {!providers && (
                                        <TableRow>
                                            <TableCell>{t('general.loading')}</TableCell>
                                        </TableRow>
                                    )}
                                    {(providers || []).map((p) => (
                                        <TableRow key={p.clusterId}>
                                            <TableCell>{p.name}</TableCell>
                                            <TableCell>
                                                <Checkbox
                                                    checked={!!p.blockIfPricingMissing}
                                                    onChange={() => toggleBlockIfPricingMissing(p)}
                                                    color="primary"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Grid>
                    </Grid>
                </div>
            </TabPanel>

            {/* Tab 1 – Products (with provider association management) */}
            <TabPanel value={tab} index={1}>
                <div className={classes.tabContent}>
                    <Grid container>
                        <Grid item xs={12}>
                            <Table className={classes.table} size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ width: 32 }} />
                                        <TableCell>{t('cargo.type')}</TableCell>
                                        <TableCell>{t('cargo.associatedProviders')}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {!products && (
                                        <TableRow>
                                            <TableCell colSpan={4}>{t('general.loading')}</TableCell>
                                        </TableRow>
                                    )}
                                    {(products || []).map((p) => {
                                        const associations = getAssociationsForProduct(p.clusterId);
                                        return (
                                            <React.Fragment key={p.clusterId}>
                                                <TableRow>
                                                    <TableCell padding="none">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleProductExpanded(p.clusterId)}>
                                                            {expandedProducts[p.clusterId] ? (
                                                                <KeyboardArrowUpIcon fontSize="small" />
                                                            ) : (
                                                                <KeyboardArrowDownIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell>{p.type}</TableCell>
                                                    <TableCell>
                                                        {associations.length === 0
                                                            ? '—'
                                                            : associations
                                                                  .map((a) => getProviderName(a.cargoProviderId))
                                                                  .join(', ')}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton size="small" onClick={() => openProductEditor(p)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell colSpan={4} style={{ paddingTop: 0, paddingBottom: 0 }}>
                                                        <Collapse in={!!expandedProducts[p.clusterId]} unmountOnExit>
                                                            <Box className={classes.detailBox}>
                                                                <Table className={classes.detailTable} size="small">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell className={classes.detailCell}>
                                                                                {t('cargo.provider')}
                                                                            </TableCell>
                                                                            <TableCell className={classes.detailCell}>
                                                                                {t('cargo.deliveryMethod')}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {associations.length === 0 && (
                                                                            <TableRow>
                                                                                <TableCell
                                                                                    colSpan={2}
                                                                                    className={classes.detailCell}>
                                                                                    —
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        )}
                                                                        {associations.map((a) => (
                                                                            <TableRow key={a.clusterId}>
                                                                                <TableCell
                                                                                    className={classes.detailCell}>
                                                                                    {getProviderName(a.cargoProviderId)}
                                                                                </TableCell>
                                                                                <TableCell
                                                                                    className={classes.detailCell}>
                                                                                    {a.deliveryMethodId
                                                                                        ? getDeliveryMethodName(
                                                                                              a.deliveryMethodId
                                                                                          )
                                                                                        : t('cargo.noDeliveryMethod')}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Grid>
                    </Grid>
                </div>
            </TabPanel>

            {/* Tab 2 – Purchase Groups */}
            <TabPanel value={tab} index={2}>
                <div className={classes.tabContent}>
                    <Grid container>
                        <Grid item xs={12}>
                            <Table className={classes.table} size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ width: 32 }} />
                                        <TableCell>{t('general.name')}</TableCell>
                                        <TableCell>{t('cargo.provider')}</TableCell>
                                        <TableCell>{t('cargo.product')}</TableCell>
                                        <TableCell>{t('cargo.rowCount')}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {!purchaseGroups && (
                                        <TableRow>
                                            <TableCell colSpan={6}>{t('general.loading')}</TableCell>
                                        </TableRow>
                                    )}
                                    {(purchaseGroups || []).map((g) => (
                                        <React.Fragment key={g.clusterId}>
                                            <TableRow>
                                                <TableCell padding="none">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => togglePurchaseGroup(g.clusterId)}>
                                                        {expandedPurchaseGroups[g.clusterId] ? (
                                                            <KeyboardArrowUpIcon fontSize="small" />
                                                        ) : (
                                                            <KeyboardArrowDownIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>{g.name}</TableCell>
                                                <TableCell>{getProviderName(g.cargoProviderId)}</TableCell>
                                                <TableCell>
                                                    {g.costRows && g.costRows[0]
                                                        ? getProductName(g.costRows[0].cargoProductId)
                                                        : ''}
                                                </TableCell>
                                                <TableCell>{g.costRows ? g.costRows.length : 0}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" onClick={() => openEditPurchaseGroup(g)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => confirmDeletePurchaseGroup(g)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={6} style={{ paddingTop: 0, paddingBottom: 0 }}>
                                                    <Collapse in={!!expandedPurchaseGroups[g.clusterId]} unmountOnExit>
                                                        <Box className={classes.detailBox}>
                                                            <Table className={classes.detailTable} size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell className={classes.detailCell}>
                                                                            {t('cargo.priceWithoutVat')}
                                                                        </TableCell>
                                                                        {renderModeHeaderCells(
                                                                            getGroupPricingMode(g, g.costRows)
                                                                        )}
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {(g.costRows || []).length === 0 && (
                                                                        <TableRow>
                                                                            <TableCell
                                                                                colSpan={modeColumnCount(
                                                                                    getGroupPricingMode(g, g.costRows)
                                                                                )}
                                                                                className={classes.detailCell}>
                                                                                —
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    )}
                                                                    {(g.costRows || []).map((row, idx) => (
                                                                        <TableRow key={idx}>
                                                                            <TableCell className={classes.detailCell}>
                                                                                {row.priceWithoutVat}
                                                                            </TableCell>
                                                                            {renderModeValueCells(
                                                                                getGroupPricingMode(g, g.costRows),
                                                                                row
                                                                            )}
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </Grid>
                        <Grid item>
                            <Button
                                className={classes.btn}
                                startIcon={<AddIcon />}
                                variant="contained"
                                color="primary"
                                onClick={openNewPurchaseGroup}
                            />
                        </Grid>
                    </Grid>
                </div>
            </TabPanel>

            {/* Tab 3 – Sales Groups */}
            <TabPanel value={tab} index={3}>
                <div className={classes.tabContent}>
                    <Grid container>
                        <Grid item xs={12}>
                            <Table className={classes.table} size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ width: 32 }} />
                                        <TableCell>{t('general.name')}</TableCell>
                                        <TableCell>{t('cargo.provider')}</TableCell>
                                        <TableCell>{t('cargo.product')}</TableCell>
                                        <TableCell>{t('cargo.rowCount')}</TableCell>
                                        <TableCell>{t('cargo.contractCount')}</TableCell>
                                        <TableCell>{t('cargo.isDefaultSalesGroup')}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {!salesGroups && (
                                        <TableRow>
                                            <TableCell colSpan={8}>{t('general.loading')}</TableCell>
                                        </TableRow>
                                    )}
                                    {(salesGroups || []).map((g) => (
                                        <React.Fragment key={g.clusterId}>
                                            <TableRow>
                                                <TableCell padding="none">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleSalesGroup(g.clusterId)}>
                                                        {expandedSalesGroups[g.clusterId] ? (
                                                            <KeyboardArrowUpIcon fontSize="small" />
                                                        ) : (
                                                            <KeyboardArrowDownIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>{g.name}</TableCell>
                                                <TableCell>{getProviderName(g.cargoProviderId)}</TableCell>
                                                <TableCell>
                                                    {getProductName(
                                                        g.cargoProductId ||
                                                            (g.priceRows &&
                                                                g.priceRows[0] &&
                                                                g.priceRows[0].cargoProductId)
                                                    )}
                                                </TableCell>
                                                <TableCell>{g.priceRows ? g.priceRows.length : 0}</TableCell>
                                                <TableCell>
                                                    {(salesGroupContractLinks[g.clusterId] || []).length}
                                                </TableCell>
                                                <TableCell>
                                                    {g.isDefault ? (
                                                        <CheckIcon fontSize="small" data-testid="DefaultIcon" />
                                                    ) : (
                                                        ''
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" onClick={() => openEditSalesGroup(g)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => confirmDeleteSalesGroup(g)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={8} style={{ paddingTop: 0, paddingBottom: 0 }}>
                                                    <Collapse in={!!expandedSalesGroups[g.clusterId]} unmountOnExit>
                                                        <Box className={classes.detailBox}>
                                                            <Table className={classes.detailTable} size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell className={classes.detailCell}>
                                                                            {t('cargo.priceWithoutVat')}
                                                                        </TableCell>
                                                                        {renderModeHeaderCells(
                                                                            getGroupPricingMode(g, g.priceRows)
                                                                        )}
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {(g.priceRows || []).length === 0 && (
                                                                        <TableRow>
                                                                            <TableCell
                                                                                colSpan={modeColumnCount(
                                                                                    getGroupPricingMode(g, g.priceRows)
                                                                                )}
                                                                                className={classes.detailCell}>
                                                                                —
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    )}
                                                                    {(g.priceRows || []).map((row, idx) => (
                                                                        <TableRow key={idx}>
                                                                            <TableCell className={classes.detailCell}>
                                                                                {row.priceWithoutVat}
                                                                            </TableCell>
                                                                            {renderModeValueCells(
                                                                                getGroupPricingMode(g, g.priceRows),
                                                                                row
                                                                            )}
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                            <Typography
                                                                variant="subtitle2"
                                                                className={classes.contractListTitle}>
                                                                {t('cargo.linkedContracts')}
                                                            </Typography>
                                                            {(salesGroupContractLinks[g.clusterId] || []).length ===
                                                            0 ? (
                                                                <Typography
                                                                    variant="body2"
                                                                    className={classes.detailCell}>
                                                                    —
                                                                </Typography>
                                                            ) : (
                                                                <div className={classes.contractChipsWrap}>
                                                                    {getSortedContractLinks(g.clusterId).map((link) => (
                                                                        <Chip
                                                                            key={link.clusterId}
                                                                            size="small"
                                                                            label={link.contractName}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </Grid>
                        <Grid item>
                            <Button
                                className={classes.btn}
                                startIcon={<AddIcon />}
                                variant="contained"
                                color="primary"
                                onClick={openNewSalesGroup}
                            />
                        </Grid>
                    </Grid>
                </div>
            </TabPanel>

            {/* Dialogs */}
            <CargoProductProviderEditor
                open={productEditorOpen}
                product={editingProduct}
                providers={providers || []}
                deliveryMethods={deliveryMethods || []}
                associations={editingProduct ? getAssociationsForProduct(editingProduct.clusterId) : []}
                onAdd={(providerId, deliveryMethodId) =>
                    addProviderAssociation(editingProduct.clusterId, providerId, deliveryMethodId)
                }
                onUpdateDeliveryMethod={updateAssociationDeliveryMethod}
                onDelete={deleteProviderAssociation}
                onClose={closeProductEditor}
            />

            <CargoPurchaseGroupEditor
                open={purchaseGroupEditorOpen}
                group={editingPurchaseGroup}
                providers={providers || []}
                products={products || []}
                providerProducts={providerProducts || []}
                existingGroups={purchaseGroups || []}
                onSave={savePurchaseGroup}
                onCancel={() => setPurchaseGroupEditorOpen(false)}
            />

            <CargoSalesGroupEditor
                open={salesGroupEditorOpen}
                group={editingSalesGroup}
                providers={providers || []}
                products={products || []}
                providerProducts={providerProducts || []}
                existingGroups={salesGroups || []}
                onSave={saveSalesGroupHandler}
                onCancel={() => setSalesGroupEditorOpen(false)}
            />

            <ConfirmationDialog
                open={confirmOpen}
                confirm={doDelete}
                cancel={() => setConfirmOpen(false)}
                confirmText={t('buttons.remove')}>
                {deleteConfirmText()}
            </ConfirmationDialog>
        </div>
    );
}
